/**
 * Spot illustrations for empty, error and success states.
 *
 * Deliberately quiet: soft geometry, a single accent, no faces or stock-art
 * flourishes. They are drawn entirely from theme tokens (`--accent`,
 * `--surface-3`, `--border-strong`), so they re-colour with light/dark mode and
 * never look pasted on. Decorative by default — the surrounding heading and
 * body text carry the meaning, so the SVG stays aria-hidden.
 *
 * Usage: <Illustration name="no-results" />
 */

const A = 'var(--accent)';
const SOFT = 'var(--accent-soft)';
const SOFT2 = 'var(--accent-soft-2)';
const LINE = 'var(--border-strong)';
const FILL = 'var(--surface-3)';
const SURF = 'var(--surface)';

/* Shared backdrop: a soft blob that grounds each scene without competing. */
const Blob = ({ cx = 100, cy = 82, rx = 76, ry = 58 }) => (
  <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill={SOFT} opacity="0.55" />
);

const SCENES = {
  /* Nothing booked yet — an empty calendar with a friendly plus. */
  'empty-bookings': (
    <>
      <Blob />
      <rect x="52" y="42" width="96" height="82" rx="12" fill={SURF} stroke={LINE} strokeWidth="2" />
      <path d="M52 62h96" stroke={LINE} strokeWidth="2" />
      <path d="M74 34v14M126 34v14" stroke={A} strokeWidth="3.5" strokeLinecap="round" />
      <rect x="66" y="74" width="20" height="14" rx="4" fill={FILL} />
      <rect x="92" y="74" width="20" height="14" rx="4" fill={SOFT2} />
      <rect x="118" y="74" width="20" height="14" rx="4" fill={FILL} />
      <rect x="66" y="96" width="20" height="14" rx="4" fill={FILL} />
      <circle cx="132" cy="110" r="17" fill={A} />
      <path d="M132 103v14M125 110h14" stroke={SURF} strokeWidth="2.6" strokeLinecap="round" />
    </>
  ),

  /* Search returned nothing — a magnifier over an empty list. */
  'no-results': (
    <>
      <Blob />
      <rect x="46" y="46" width="82" height="74" rx="12" fill={SURF} stroke={LINE} strokeWidth="2" />
      <rect x="60" y="62" width="40" height="7" rx="3.5" fill={FILL} />
      <rect x="60" y="78" width="54" height="7" rx="3.5" fill={FILL} />
      <rect x="60" y="94" width="30" height="7" rx="3.5" fill={FILL} />
      <circle cx="130" cy="76" r="27" fill={SURF} fillOpacity="0.9" stroke={A} strokeWidth="3.5" />
      <path d="M150 96l14 14" stroke={A} strokeWidth="5" strokeLinecap="round" />
      <path d="M121 76h18" stroke={A} strokeWidth="3" strokeLinecap="round" opacity="0.5" />
    </>
  ),

  /* Nothing in the inbox / no requests waiting. */
  inbox: (
    <>
      <Blob />
      <path
        d="M44 96l12-40a8 8 0 0 1 7.7-5.8h72.6A8 8 0 0 1 144 56l12 40v24a8 8 0 0 1-8 8H52a8 8 0 0 1-8-8V96Z"
        fill={SURF}
        stroke={LINE}
        strokeWidth="2"
      />
      <path d="M44 96h30l6 12h40l6-12h30" stroke={A} strokeWidth="3" strokeLinejoin="round" fill="none" />
      <path d="M78 66h44M86 78h28" stroke={LINE} strokeWidth="3" strokeLinecap="round" />
    </>
  ),

  /* Documents / verification pending. */
  documents: (
    <>
      <Blob />
      <rect x="58" y="34" width="70" height="88" rx="10" fill={FILL} stroke={LINE} strokeWidth="2" transform="rotate(-7 93 78)" />
      <rect x="72" y="38" width="70" height="88" rx="10" fill={SURF} stroke={LINE} strokeWidth="2" />
      <path d="M86 60h42M86 74h42M86 88h26" stroke={LINE} strokeWidth="3" strokeLinecap="round" />
      <circle cx="130" cy="104" r="18" fill={A} />
      <path d="M122 104l6 6 10-11" stroke={SURF} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),

  /* Something worked — a drawn check inside a soft ring. */
  success: (
    <>
      <Blob />
      <circle cx="100" cy="80" r="42" fill={SOFT2} />
      <circle cx="100" cy="80" r="30" fill={A} />
      <path
        className="draw-check"
        d="M87 80l9 9 18-19"
        stroke={SURF}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path d="M148 44l3 7 7 3-7 3-3 7-3-7-7-3 7-3 3-7Z" fill={A} opacity="0.6" />
      <path d="M46 96l2 5 5 2-5 2-2 5-2-5-5-2 5-2 2-5Z" fill={A} opacity="0.4" />
    </>
  ),

  /* No connection / could not load. */
  offline: (
    <>
      <Blob />
      <path d="M56 84a44 44 0 0 1 88 0" stroke={LINE} strokeWidth="4" strokeLinecap="round" fill="none" />
      <path d="M72 98a28 28 0 0 1 56 0" stroke={LINE} strokeWidth="4" strokeLinecap="round" fill="none" />
      <path d="M88 112a14 14 0 0 1 24 0" stroke={A} strokeWidth="4" strokeLinecap="round" fill="none" />
      <circle cx="100" cy="122" r="5" fill={A} />
      <path d="M52 40l100 92" stroke={SURF} strokeWidth="9" strokeLinecap="round" />
      <path d="M52 40l100 92" stroke={A} strokeWidth="4" strokeLinecap="round" />
    </>
  ),

  /* Route not found. */
  'not-found': (
    <>
      <Blob />
      <circle cx="100" cy="80" r="40" fill={SURF} stroke={LINE} strokeWidth="2" />
      <path d="M100 40v80M60 80h80" stroke={FILL} strokeWidth="2" />
      <path d="m112 62-8 22-22 8 8-22 22-8Z" fill={A} />
      <circle cx="100" cy="80" r="40" stroke={A} strokeWidth="3" fill="none" strokeDasharray="6 10" opacity="0.5" />
    </>
  ),

  /* No availability published. */
  schedule: (
    <>
      <Blob />
      <rect x="50" y="44" width="100" height="80" rx="12" fill={SURF} stroke={LINE} strokeWidth="2" />
      <path d="M50 66h100" stroke={LINE} strokeWidth="2" />
      <path d="M74 36v14M126 36v14" stroke={A} strokeWidth="3.5" strokeLinecap="round" />
      <rect x="64" y="78" width="34" height="10" rx="5" fill={SOFT2} />
      <rect x="106" y="78" width="30" height="10" rx="5" fill={FILL} />
      <rect x="64" y="96" width="24" height="10" rx="5" fill={FILL} />
      <rect x="96" y="96" width="40" height="10" rx="5" fill={SOFT2} />
    </>
  ),

  /* Earnings / money. */
  earnings: (
    <>
      <Blob />
      <rect x="46" y="70" width="30" height="52" rx="6" fill={FILL} />
      <rect x="85" y="52" width="30" height="70" rx="6" fill={SOFT2} />
      <rect x="124" y="34" width="30" height="88" rx="6" fill={A} />
      <path d="M40 122h120" stroke={LINE} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M52 46l22 12 20-16 26 10" stroke={A} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.45" />
    </>
  ),

  /* Complaints / support. */
  support: (
    <>
      <Blob />
      <rect x="44" y="44" width="86" height="60" rx="14" fill={SURF} stroke={LINE} strokeWidth="2" />
      <path d="M64 104l-2 18 20-18" fill={SURF} stroke={LINE} strokeWidth="2" strokeLinejoin="round" />
      <path d="M62 66h50M62 80h32" stroke={LINE} strokeWidth="3" strokeLinecap="round" />
      <circle cx="140" cy="92" r="22" fill={A} />
      <path d="M140 84v9M140 100h.01" stroke={SURF} strokeWidth="3" strokeLinecap="round" />
    </>
  ),

  /* Profile completeness. */
  profile: (
    <>
      <Blob />
      <circle cx="100" cy="66" r="24" fill={SOFT2} />
      <path d="M62 122a38 38 0 0 1 76 0" fill={SOFT2} />
      <circle cx="100" cy="66" r="24" stroke={A} strokeWidth="3" fill="none" />
      <path d="M62 122a38 38 0 0 1 76 0" stroke={A} strokeWidth="3" fill="none" />
      <circle cx="136" cy="102" r="16" fill={A} stroke={SURF} strokeWidth="3" />
      <path d="M129 102l5 5 9-10" stroke={SURF} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),

  /* Care at home — used on auth and marketing surfaces. */
  care: (
    <>
      <Blob />
      <path d="M52 88l48-38 48 38" stroke={A} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M64 84v34a4 4 0 0 0 4 4h64a4 4 0 0 0 4-4V84" fill={SURF} stroke={LINE} strokeWidth="2" />
      <path d="M92 122V98h16v24" fill={SOFT2} stroke={LINE} strokeWidth="2" />
      <path d="M100 60v14M93 67h14" stroke={A} strokeWidth="4" strokeLinecap="round" />
    </>
  ),
};

export default function Illustration({ name, className = '', width = 200 }) {
  const scene = SCENES[name];
  if (!scene) return null;
  return (
    <svg
      viewBox="0 0 200 150"
      width={width}
      className={className}
      aria-hidden="true"
      focusable="false"
      style={{ maxWidth: '100%', height: 'auto' }}
    >
      {scene}
    </svg>
  );
}

export const ILLUSTRATIONS = Object.keys(SCENES);
